import { FC, KeyboardEvent, useState, MouseEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";

import type { AircraftOperationCompliance } from "src/shared/api-types/certificate";
import CourseStatusIcon from "src/components/CourseStatusIcon";
import certificateStatusService from "src/services/certificateStatus";

interface AircraftOverviewTooltipProps {
  compliance: AircraftOperationCompliance;
  operationName: string;
  aircraftName: string;
  // Clicking the status opens the course details for it, anchored at the status
  onOpenDetails: (anchor: HTMLElement) => void;
}

const labelSx = {
  color: "rgba(255, 255, 255, 0.7)",
  textTransform: "uppercase" as const,
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.5px",
};

const valueSx = {
  color: "#fff",
  fontWeight: 500,
  mt: 0.25,
};

const AircraftOverviewTooltip: FC<AircraftOverviewTooltipProps> = ({
  compliance,
  operationName,
  aircraftName,
  onOpenDetails,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // The details popover replaces the tooltip
  const openDetails = (anchor: HTMLElement) => {
    setOpen(false);
    onOpenDetails(anchor);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    openDetails(event.currentTarget);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openDetails(event.currentTarget);
  };

  const handleMouseEnter = () => setOpen(true);

  const handleMouseLeave = () => setOpen(false);

  const operationStatusLabel = useMemo(
    () => certificateStatusService.getByCode(compliance.operation.status as any)?.getDisplayName(t) ?? "—",
    [compliance.operation.status, t],
  );

  const aircraftStatusLabel = useMemo(
    () => certificateStatusService.getByCode(compliance.aircraft.status as any)?.getDisplayName(t) ?? "—",
    [compliance.aircraft.status, t],
  );
  const combinedStatusLabel =
    certificateStatusService.getByCode(compliance.combined.status as any)?.getDisplayName(t) ?? "—";
  const limitations = compliance.limitations ?? [];

  const tooltipContent = (
    <Box sx={{ minWidth: 160 }}>
      {/* Operation status */}
      <Box
        sx={{
          mb: 1.5,
        }}
      >
        <Typography variant="caption" sx={labelSx}>
          {operationName}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mt: 0.25,
          }}
        >
          <CourseStatusIcon status={compliance.operation.status} />
          <Typography variant="body2" sx={valueSx}>
            {operationStatusLabel}
          </Typography>
        </Box>
        {compliance.operation.validUntil && (
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block", mt: 0.25 }}>
            {compliance.operation.validUntil}
          </Typography>
        )}
      </Box>

      {/* Aircraft status */}
      <Box
        sx={{
          mb: compliance.underSupervision || limitations.length > 0 ? 1.5 : 0,
        }}
      >
        <Typography variant="caption" sx={labelSx}>
          {aircraftName}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mt: 0.25,
          }}
        >
          <CourseStatusIcon status={compliance.aircraft.status} />
          <Typography variant="body2" sx={valueSx}>
            {aircraftStatusLabel}
          </Typography>
        </Box>
        {compliance.aircraft.validUntil && (
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block", mt: 0.25 }}>
            {compliance.aircraft.validUntil}
          </Typography>
        )}
      </Box>

      {/* Under supervision note */}
      {compliance.underSupervision && (
        <Box
          sx={{
            mt: 0.5,
            mb: limitations.length > 0 ? 1.5 : 0,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: "#4254bc",
          }}
        >
          <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>
            {t("aircraft_management.under_supervision")}
          </Typography>
        </Box>
      )}

      {limitations.length > 0 && (
        <Box>
          <Typography variant="caption" sx={labelSx}>
            {t("limitations.title")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            {limitations.map((limitation) => (
              <Chip
                key={limitation.definitionId}
                label={limitation.displayValue}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  color: "#fff",
                  backgroundColor: "#4254bc",
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  const icon = (
    <CourseStatusIcon
      status={compliance.combined.status}
      underSupervision={compliance.underSupervision}
      hasLimitations={limitations.length > 0}
      showTooltip={false}
    />
  );

  return (
    <span style={{ display: "inline-flex", padding: 4 }}>
      <Tooltip
        open={open}
        title={tooltipContent}
        placement="top"
        arrow
        disableFocusListener
        disableTouchListener
        disableHoverListener
        slotProps={{
          popper: {
            disablePortal: false,
            sx: {
              "& .MuiTooltip-tooltip": {
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                color: "#fff",
                padding: 1.5,
                maxWidth: 300,
                borderRadius: "4px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
              },
              "& .MuiTooltip-arrow": {
                color: "rgba(0, 0, 0, 0.85)",
              },
            },
          },
        }}
      >
        <span
          role="button"
          tabIndex={0}
          aria-haspopup="dialog"
          // The icon is hidden from assistive technology and the tooltip names the trigger only while open
          aria-label={`${operationName} · ${aircraftName}: ${combinedStatusLabel}`}
          style={{ display: "inline-flex", cursor: "pointer" }}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {icon}
        </span>
      </Tooltip>
    </span>
  );
};

export default AircraftOverviewTooltip;
